import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { articles, categories } from "@/mocks/content";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = categories.find((c) => c.slug === slug);
  const [sort, setSort] = useState<"new" | "hot">("new");

  const list = useMemo(() => {
    const result = articles.filter((a) => a.categorySlug === slug);
    result.sort((a, b) => (sort === "hot" ? b.views - a.views : a.date < b.date ? 1 : -1));
    return result;
  }, [slug, sort]);

  const catName = category?.name ?? "未知分类";

  useDocumentMeta({
    title: category
      ? `${catName} - 看料91 热门爆料与现场记录`
      : "分类不存在 - 看料91",
    description: category
      ? `看料91 ${catName}分类，整理该主题下的热门爆料与现场记录，支持翻看、阅读、查看完整内容，持续更新。`
      : "该分类暂不存在，返回看料91首页浏览其他热门内容。",
    keywords: category ? `看料91,${catName},看料91浏览,看料91翻看,热门爆料` : "看料91",
    path: `/category/${slug}`,
    jsonLd: category
      ? {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${catName} - 看料91`,
          description: category.desc,
          inLanguage: "zh-CN",
          url: `https://fxwlkj.cn/category/${slug}`,
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://fxwlkj.cn/" },
              { "@type": "ListItem", position: 2, name: catName, item: `https://fxwlkj.cn/category/${slug}` },
            ],
          },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: list.map((a, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: a.title,
              url: `https://fxwlkj.cn/article/${a.id}`,
            })),
          },
        }
      : undefined,
  });

  if (!category) {
    return (
      <div className="w-full px-4 md:px-8 pt-32 pb-24 text-center">
        <span className="w-14 h-14 mx-auto flex items-center justify-center text-foreground-300">
          <i className="ri-emotion-unhappy-line text-5xl" />
        </span>
        <h1 className="font-heading text-2xl font-black text-foreground-950 mt-4">
          该分类暂不存在
        </h1>
        <p className="text-sm text-foreground-600 mt-3">换个主题试试，或返回首页浏览最新内容。</p>
        <Link
          to="/"
          className="inline-block mt-6 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm hover:bg-primary-600 transition-colors cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb items={[{ label: "分类", to: "/" }, { label: catName }]} />

      <section className="w-full px-4 md:px-8">
        <div className="bg-secondary-900 rounded-lg px-5 md:px-10 py-10 md:py-14">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500/15 text-accent-400">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-black text-white">
              {catName}
            </h1>
          </div>
          <p className="text-sm md:text-base text-secondary-100/85 mt-4 max-w-2xl leading-relaxed">
            {category.desc}
          </p>
          <p className="text-xs md:text-sm text-secondary-200/70 mt-3">
            共 {category.count} 条内容 · 本页展示 {list.length} 条
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
            {catName} · 内容列表
          </h2>
          <div className="flex bg-background-100 border border-background-300 rounded-lg p-1 self-start">
            {(["new", "hot"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSort(s)}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  sort === s
                    ? "bg-secondary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {s === "new" ? "最新" : "最热"}
              </button>
            ))}
          </div>
        </div>

        {list.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {list.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center bg-background-100 rounded-lg">
            <p className="text-sm text-foreground-500">这个分类的内容正在整理中，稍后再来看看。</p>
          </div>
        )}
      </section>

      <section className="w-full px-4 md:px-8 pb-16">
        <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5">换个主题看看</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {categories
            .filter((c) => c.slug !== slug)
            .map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="flex items-center gap-2 px-4 py-3 rounded-lg bg-background-100 border border-transparent hover:border-secondary-300 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center text-secondary-600">
                  <i className={`${c.icon} text-lg`} />
                </span>
                <span className="text-sm text-foreground-800">{c.name}</span>
              </Link>
            ))}
        </div>
      </section>
    </>
  );
}